import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChartPie, ClipboardList, Pencil, Plus, Settings2, Trash2 } from 'lucide-react'
import Button from '../../components/Button'
import { useRemembered } from '../../components/listMemory'
import ConfirmDialog from '../../components/ConfirmDialog'
import DataTable from '../../components/DataTable'
import SectionNav from '../../components/SectionNav'
import { DeniedState, ErrorState } from '../../components/States'
import { SwitchField } from '../../components/Form'
import useHashTab from '../../components/useHashTab'
import useFeatureToggle from '../../components/useFeatureToggle'
import FilterBar from '../../components/FilterBar'
import PageHeader from '../../components/PageHeader'
import RowActions from '../../components/RowActions'
import { useToast } from '../../components/toastContext'
import { dateColumn, flagColumn, indexColumn, statusColumn } from './cells'
import {
  deleteAssessment,
  getAssessmentsPage,
  setAssessmentsEnabled,
  listAssessments,
} from './engagementService'
import { assessmentsStrings as S } from './strings'
import './engagement.css'
import { serverDetail, serverText } from '../../lib/i18n'

/** إدارة القياسات — Views/AssessmentsManagement/Index.cshtml. */
export default function AssessmentsPage() {
  const toast = useToast()
  const navigate = useNavigate()
  const [featureOn, setFeatureOn] = useState(true)
  /* The two sections, named in the URL hash like every other tabbed screen. */
  const [tab, setTab] = useHashTab('assessments', ['assessments', 'settings'])
  /* REMEMBERED, not plain state: the table's page came back on a Back and the search
     that chose those rows did not, so page 3 returned over an unsearched list. */
  const [query, setQuery] = useRemembered('assessments:query', '')
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  /* The load's REFUSAL, kept rather than only toasted — see the branch below the columns. */
  const [loadError, setLoadError] = useState(null)
  const [pendingDelete, setPendingDelete] = useState(null)

  /* The form has its own screen. The row travels with the click so an edit opens filled in
     with no second request; `AssessmentFormPage` falls back to `getAssessment` when the URL
     is reached directly. */
  const openForm = useCallback((assessment) =>
    navigate(
      assessment ? `/assessments-management/${assessment.id}/edit` : '/assessments-management/new',
      assessment ? { state: { assessment } } : undefined,
    ), [navigate])

  const [reloadKey, setReloadKey] = useState(0)

  /* Only the feature switch is read here now. The topics and groups this screen used to
     carry existed for the form, and the form fetches its own — see `AssessmentFormPage`. */
  useEffect(() => {
    getAssessmentsPage()
      .then((page) => setFeatureOn(page.enabled))
      .catch(() => {})
  }, [])

  /* Optimistic, and put back if the server refuses. */
  /* Shared with every other activation switch — see useFeatureToggle. */
  const { toggle: toggleFeature } = useFeatureToggle({
    setValue: setFeatureOn,
    save: setAssessmentsEnabled,
  })

  useEffect(() => {
    setLoading(true)
    listAssessments()
      .then((response) => {
        setLoadError(null)
        setRows(response.rows)
        setLoading(false)
      })
      .catch((error) => {
        setLoadError(error)
        setRows([])
        setLoading(false)
        toast.error(serverDetail(error?.message) || S.fail.list)
      })
  }, [reloadKey, toast])

  const columns = useMemo(
    () => [
      indexColumn(S.columns.index),
      {
        key: 'title',
        header: S.columns.title,
        render: (row) => (
          <bdi className="eng__title" title={row.title}>
            {row.title}
          </bdi>
        ),
      },
      dateColumn('displayStartDate', S.columns.startDate),
      dateColumn('displayEndDate', S.columns.endDate),
      statusColumn('isActive', S.columns.status, S),
      flagColumn('mandatory', S.columns.mandatory, S),
      { key: 'passed', header: S.columns.passed, align: 'center' },
      { key: 'failed', header: S.columns.failed, align: 'center' },
      { key: 'absent', header: S.columns.absent, align: 'center' },
      {
        key: 'actions',
        header: <Pencil size={15} strokeWidth={1.75} />,
        align: 'center',
        width: '112px',
        sortable: false,
        searchable: false,
        render: (row) => (
          <RowActions
            actions={[
              /* THE RESULTS. `التفاصيل` used to be missing from this row because the screen
                 behind it did not exist — the legacy borrows the course exam report for it,
                 and there was no port of that. There is now. */
              {
                id: 'report',
                icon: ChartPie,
                label: S.row.details,
                tone: 'info',
                onClick: () => navigate(`/assessments-management/${row.id}/report`),
              },
              { id: 'update', icon: Pencil, label: S.row.update, onClick: () => openForm(row) },
              /* `إرسال بريد` IS NOT HERE. Nothing in this slice sends one — the
                 legacy's send action has no counterpart in the port, and a mail button
                 that opens nothing is worse than its absence. COSMETIC_UI.md. */
              {
                id: 'delete',
                icon: Trash2,
                label: S.row.delete,
                danger: true,
                onClick: () => setPendingDelete(row),
              },
            ]}
          />
        ),
      },
    ],
    [navigate, openForm],
  )

  /* A FAILED load is not an empty one. Without this the table rendered "لا توجد مدخلات في
     النظام" for a 403, so a user without AssessmentManagers was told the system holds no
     assessments — and was still offered the button to add one. The policies screen next door
     already did this; only its sibling was left behind. */
  if (loadError) {
    return (
      <>
        <PageHeader title={S.title} />
        {loadError.denied ? (
          <DeniedState description={loadError.message} />
        ) : (
          <ErrorState title={S.actionFailed} description={loadError.message} />
        )}
      </>
    )
  }

  return (
    <>
      <PageHeader
        title={S.title}
        count={tab === 'assessments' && !loading ? rows.length : undefined}
        actions={
          tab === 'assessments' && (
            <Button onClick={() => openForm(null)}>
              <Plus size={16} strokeWidth={2} />
              {S.create}
            </Button>
          )
        }
      />

      <div className="eng__workspace">
        <SectionNav
          groups={[
            {
              items: [
                { id: 'assessments', label: S.sections.list, icon: ClipboardList },
                { id: 'settings', label: S.sections.settings, icon: Settings2 },
              ],
            },
          ]}
          value={tab}
          onChange={setTab}
          ariaLabel={S.title}
        />

        <div className="eng__panel">
          {tab === 'assessments' && (
            <>
            <FilterBar search={{ value: query, onChange: setQuery, placeholder: S.columns.title }} />

            <DataTable
              columns={columns}
              rows={rows}
              loading={loading}
              query={query}
              controls={false}
              getRowId={(row) => row.id}
              rowTo={(row) => `/assessments-management/${row.id}/report`}
              initialSort={{ key: 'startDate', dir: 'desc' }}
            />
            </>
          )}

          {/* The feature's own switch: a system setting, so it lives with the settings
              rather than in the page head — where the legacy kept it. It saves at once. */}
          {tab === 'settings' && (
            <SwitchField feature checked={featureOn} onChange={toggleFeature} label={S.enableFeature} />
          )}
        </div>
      </div>


      <ConfirmDialog
        open={!!pendingDelete}
        message={S.deleteConfirm}
        confirmLabel={S.row.delete}
        cancelLabel={S.close}
        onConfirm={async () => {
          const outcome = await deleteAssessment(pendingDelete.id)
          if (!outcome.ok) return toast.error(serverDetail(outcome.message) || S.fail.delete)
          setPendingDelete(null)
          toast.success(serverText(outcome.message) || S.deleted)
          setReloadKey((key) => key + 1)
        }}
        onCancel={() => setPendingDelete(null)}
      />
    </>
  )
}
